import { Box, Flex, HStack, Image, Tag, Text } from '@chakra-ui/react';
import { GlobalSearchArticleItem } from '@texas/api/endpoints/search/searchApi';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { clientEndpoints } from '@texas/clientEndpoints';
import { ExpandImage } from '../shared/image/ExpandImage';
import placeholderImg from '@texas/assets/placeholder-50.png';
import { useTranslation } from 'react-i18next';

interface Props {
  article: GlobalSearchArticleItem;
  onResultClick: () => void;
}

export function ArticleItem({ article, onResultClick }: Props) {
  const { t } = useTranslation();

  return (
    <AngularNavLink
      role="group"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={2}
      borderRadius="md"
      _hover={{
        bg: 'whiteAlpha.100',
        _light: { bg: 'blackAlpha.100' },
      }}
      to="article"
      navobj={{ articleId: article.id }}
      onClick={onResultClick}
      textDecoration="none"
    >
      <HStack>
        <ExpandImage identifier={article.imageIdentifier} variant="scale">
          <Image
            boxSize="12"
            src={
              article.imageIdentifier
                ? clientEndpoints.previewImage(article.imageIdentifier, 128)
                : undefined
            }
            fallbackSrc={placeholderImg}
            objectFit="contain"
            borderRadius="md"
          />
        </ExpandImage>
        <Box>
          <HStack>
            <Text fontWeight="bold">{article.itemCode}</Text>
            {article.archived && (
              <Tag size="sm" colorScheme="orange">
                {t('general.archived')}
              </Tag>
            )}
            {article.isMatrix && (
              <Tag size="sm" colorScheme="purple">
                {t('general.matrix')}
              </Tag>
            )}
            {article.isVariable && (
              <Tag size="sm" colorScheme="teal">
                {t('general.variable')}
              </Tag>
            )}
          </HStack>
          <Text fontSize="sm">{article.name}</Text>
        </Box>
      </HStack>
      <Flex gap={2} _groupHover={{ opacity: 1 }} opacity={0}>
        <AngularNavLink
          to="variant.options"
          onClick={onResultClick}
          navobj={{
            articleId: article.id,
            variantId: article.productDevelopmentVariantId,
          }}
        >
          <Text fontSize="sm">{t('variant.overview.options.title')}</Text>
        </AngularNavLink>
        <AngularNavLink
          to="variant.suppliers"
          onClick={onResultClick}
          navobj={{
            articleId: article.id,
            variantId: article.productDevelopmentVariantId,
          }}
        >
          <Text fontSize="sm">{t('variant.overview.suppliers.title')}</Text>
        </AngularNavLink>
        {article.mainVariantId && (
          <>
            <AngularNavLink
              to="variant.purchase"
              onClick={onResultClick}
              navobj={{
                articleId: article.id,
                variantId: article.mainVariantId,
              }}
            >
              <Text fontSize="sm">{t('variant.overview.purchase.title')}</Text>
            </AngularNavLink>
            <AngularNavLink
              to="variant.sales"
              onClick={onResultClick}
              navobj={{
                articleId: article.id,
                variantId: article.mainVariantId,
              }}
            >
              <Text fontSize="sm">{t('variant.overview.sales.title')}</Text>
            </AngularNavLink>
            <AngularNavLink
              to="variant.general.garp"
              onClick={onResultClick}
              navobj={{
                articleId: article.id,
                variantId: article.mainVariantId,
              }}
            >
              <Text fontSize="sm">{t('variant.overview.garp.title')}</Text>
            </AngularNavLink>
          </>
        )}
      </Flex>
    </AngularNavLink>
  );
}
